import { ComponentType } from 'react'

import { clsx } from 'clsx'

import { Image, Media, Emoji } from '@tribeplatform/gql-client/types'

import { IconEmoji, IconEmojiSize } from './IconEmoji.js'

type WithImage = { image?: Media | null; name: string }
export type EntityImageSize = 'xxs' | 'xs' | 'xsm' | 'sm' | 'md' | 'lg'

const DefaultEmoji = 'speech_balloon'
export const EntityImage = ({
  entity,
  size = 'md',
  defaultIcon: Icon,
  as,
}: {
  entity: WithImage
  size: EntityImageSize
  defaultIcon?: ComponentType<any>
  as?: 'div' | 'span'
}) => {
  const Component = as || 'div'

  const sizeClsx = [
    size === 'xxs' && 'h-4 w-4',
    size === 'xs' && 'h-6 w-6',
    size === 'xsm' && 'h-8 w-8',
    size === 'sm' && 'h-10 w-10',
    size === 'md' && 'h-16 w-16',
    size === 'lg' && 'h-24 w-24',
  ]

  const sizeInPixel = {
    xxs: 16,
    xs: 24,
    xsm: 32,
    sm: 40,
    md: 64,
    lg: 96,
  }

  const emojiSizeMap: Record<EntityImageSize, IconEmojiSize> = {
    xxs: 'xs',
    xs: 'xs',
    xsm: 'sm',
    sm: 'md',
    md: 'lg',
    lg: '2xl',
  }
  if ((entity?.image as Image)?.url) {
    return (
      <Component className={clsx('flex items-center justify-center', sizeClsx)}>
        <img
          alt={entity.name}
          className={clsx(
            'text-content-subdued group-hover:text-content-hovered object-scale-down',
            'flex-shrink-0 rounded-md',
            sizeClsx,
            size === 'lg' ? 'rounded-md p-1' : '',
          )}
          width={sizeInPixel[size] || 24}
          height={sizeInPixel[size] || 24}
          aria-hidden="true"
          src={
            size === 'lg' || size === 'md'
              ? (entity?.image as Image)?.urls?.small
              : (entity?.image as Image)?.urls?.thumb
          }
        />
      </Component>
    )
  }

  if ((entity.image as Emoji)?.text) {
    return (
      <Component
        className={clsx(
          'text-content-subdued group-hover:text-content-hovered',
          'flex items-center justify-center cursor-default',
          sizeClsx,
        )}
      >
        <IconEmoji
          text={(entity?.image as Emoji)?.text || DefaultEmoji}
          size={emojiSizeMap[size]}
        />
      </Component>
    )
  }
  return (
    <Component
      className={clsx(
        'text-content-subdued group-hover:text-content-hovered',
        'flex items-center justify-center cursor-default',
        sizeClsx,
      )}
    >
      {Icon ? (
        <Icon className={clsx(sizeClsx)} />
      ) : (
        <IconEmoji text={DefaultEmoji} size={emojiSizeMap[size]} />
      )}
    </Component>
  )
}
